import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {PropsWithChildren} from 'react'
import {createContext, use, useEffect, useMemo, useState} from 'react'

import {MergeMethod} from '../types'
import {useMergeBoxPageDataQueryKey} from '../page-data/loaders/use-merge-box-page-data'
import {useQueryClient} from '@github-ui/react-query'

interface MergeMethodContextData {
  mergeMethod: MergeMethod
  setMergeMethod: (mergeMethod: MergeMethod) => void
}

const defaultMergeMethodContext = {
  mergeMethod: MergeMethod.MERGE,
  setMergeMethod: () => {},
}

export const MergeMethodContext = createContext<MergeMethodContextData>(defaultMergeMethodContext)

export function MergeMethodContextProvider({
  children,
  defaultMergeMethod,
}: PropsWithChildren<{defaultMergeMethod?: MergeMethod}>) {
  const [mergeMethod, setMergeMethod] = useState<MergeMethod | undefined>(defaultMergeMethod)
  if (mergeMethod === undefined && defaultMergeMethod !== undefined) {
    setMergeMethod(defaultMergeMethod)
  }
  const mergeBoxPageDataQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (mergeMethod === undefined) return

    // Refetch MergeBox data when merge method changes
    queryClient.invalidateQueries({queryKey: mergeBoxPageDataQueryKey})

    // Refetch stack mergeability data for all stacked PRs
    queryClient.invalidateQueries({queryKey: [PageData.stackMergeability]})

    // We don't want this useEffect to listen to dynamic Tan Stack Query Keys
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergeMethod])

  const value = useMemo(
    () =>
      mergeMethod !== undefined
        ? {
            mergeMethod,
            setMergeMethod,
          }
        : defaultMergeMethodContext,
    [mergeMethod],
  )

  return <MergeMethodContext value={value}>{children}</MergeMethodContext>
}

export function useMergeMethodContext(): MergeMethodContextData {
  const contextData = use(MergeMethodContext)
  return contextData
}
